/**
 * What the Send Monitor says when a send does not go through.
 *
 * The service answers with a short code. A code is right for a log and wrong for
 * a person: somebody watching a send fail needs to know whether to fix
 * something, wait, or leave it alone. Each sentence below answers that.
 *
 * `canRetry` is the same question in a form the screen can act on. Offering a
 * retry for a refusal that will never change wastes somebody's afternoon, and
 * for a withdrawal it invites them to override a decision that is not theirs.
 */

const KNOWN = {
    NO_BINDING: {
        title: 'This channel is not set up',
        detail: 'Nobody has added a sending identity for this channel yet.',
        canRetry: false,
    },
    BINDING_PAUSED: {
        title: 'This channel is paused',
        detail: 'New sends are stopped. Conversations already running will finish.',
        canRetry: false,
    },
    WITHDRAWN: {
        title: 'They asked you to stop',
        detail: 'This person withdrew from your messages on this channel. The refusal is theirs and stands.',
        canRetry: false,
    },
    IDENTITY_BUSY: {
        title: 'They are already in a survey',
        detail: 'This person has another survey open on this channel. Only one runs at a time.',
        canRetry: true,
    },
    NO_TEMPLATE: {
        title: 'No approved wording',
        detail: 'The platform has not approved an invitation template for this binding yet.',
        canRetry: false,
    },
    NO_RATE: {
        title: 'No published price',
        detail: 'There is no approved rate for this destination, so the send was stopped rather than billed at a guess.',
        canRetry: false,
    },
    PROVIDER_REFUSED: {
        title: 'The provider refused it',
        detail: 'The messaging provider would not accept the message.',
        canRetry: true,
    },
    INVALID_ADDRESS: {
        title: 'The address will not work',
        detail: 'The number or address recorded for this person is not one this channel accepts.',
        canRetry: false,
    },
    RATE_LIMITED: {
        title: 'Too many at once',
        detail: 'The provider is limiting how fast messages go out. The rest are queued.',
        canRetry: true,
    },
};

/**
 * A failure code turned into something a customer can read.
 *
 * An unrecognised code shows whatever detail came with it rather than inventing
 * a reason. Saying the wrong thing confidently is worse than admitting nothing
 * was recorded, because somebody will act on it.
 */
export function sendFailureCopy(code, detail = '') {
    const extra = typeof detail === 'string' ? detail.trim() : '';
    const known = KNOWN[code];

    if (known) {
        return {
            title: known.title,
            detail: extra ? `${known.detail} ${extra}` : known.detail,
            canRetry: known.canRetry,
        };
    }

    return {
        title: 'The send did not go through',
        detail: extra || 'No reason was recorded.',
        canRetry: true,
    };
}
